import { MenuItemLink } from "@/components/Link/index.js";
import { useAuth0 } from "@auth0/auth0-react";
import GavelIcon from "@mui/icons-material/Gavel";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import RateReviewIcon from "@mui/icons-material/RateReview";
import {
    AppBar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
} from "@mui/material";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { bindMenu, bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import type { ReactNode } from "react";

const Root = (): ReactNode => {
    const { logout } = useAuth0();
    const popupState = usePopupState({ variant: "popover", popupId: "navigation-menu" });

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" component="div">
                        Wingert Auction Portal
                    </Typography>
                    <IconButton
                        color="inherit"
                        edge="end"
                        sx={{ ml: "auto" }}
                        {...bindTrigger(popupState)}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Menu {...bindMenu(popupState)}>
                        <MenuItemLink
                            to="/manage/auctions"
                            onClick={() => {
                                popupState.close();
                            }}
                        >
                            <ListItemIcon>
                                <GavelIcon />
                            </ListItemIcon>
                            <ListItemText>Auctions</ListItemText>
                        </MenuItemLink>
                        <MenuItemLink
                            to="/manage/reviews"
                            onClick={() => {
                                popupState.close();
                            }}
                        >
                            <ListItemIcon>
                                <RateReviewIcon />
                            </ListItemIcon>
                            <ListItemText>Reviews</ListItemText>
                        </MenuItemLink>
                        <Divider />
                        <MenuItem
                            onClick={() => {
                                popupState.close();
                                void logout({
                                    logoutParams: {
                                        returnTo: `${window.location.origin}/manage`,
                                    },
                                });
                            }}
                        >
                            <ListItemIcon>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText>Logout</ListItemText>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>
            <Box component="main" sx={{ py: 2 }}>
                <Outlet />
            </Box>
        </>
    );
};

export const Route = createFileRoute("/manage")({
    component: Root,
    beforeLoad: async ({ context, location }) => {
        const result = await context.auth0LoadPromise;

        if (result.error) {
            throw result.error;
        }

        if (!result.isAuthenticated) {
            await result.loginWithRedirect({
                appState: {
                    returnTo: location.href,
                },
            });
            return new Promise(() => {
                // Will never resolve
            });
        }
    },
});
